import React, { useEffect, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { LayoutDashboard, Sparkles, ScrollText, LogOut, Wallet, Send, Download , Megaphone } from 'lucide-react';
import { useAuth } from '@/lib/auth';
import { BRAND_LOGO } from '@/lib/logos';
import { InstallAppButton } from '@/components/InstallAppButton';

export function AppShell({ children }) {
    const { user, logout } = useAuth();
    const navigate = useNavigate();
    const location = useLocation();
    const navs = [
        { to: '/dashboard', icon: LayoutDashboard, label: 'Dashboard', testid: 'user-nav-dashboard' },
        { to: '/recharge', icon: Sparkles, label: 'Recharge', testid: 'user-nav-recharge' },
        ...(user?.stock_wallet_unlocked ? [
            { to: '/dashboard?tab=stock', icon: Wallet, label: 'Stock Wallet', testid: 'user-nav-stock' },
            { to: '/dashboard?tab=stock-recharge', icon: Send, label: 'Stock Recharge', testid: 'user-nav-stock-recharge' },
        ] : []),
        { to: '/dashboard?tab=history', icon: ScrollText, label: 'History', testid: 'user-nav-history' },
        { to: '/promote', icon: Megaphone, label: 'Promote', testid: 'user-nav-promote' },
    ];
    return (
        <div className="min-h-screen bg-[#F7F8FA] flex flex-col">
            <header className="sticky top-0 z-30 border-b border-border bg-white">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
                    <Link to="/dashboard" className="flex items-center gap-2">
                        <img src={BRAND_LOGO} alt="Eagle Eyes" className="w-9 h-9 rounded-lg object-cover ring-1 ring-border" />
                        <div>
                            <div className="font-display font-semibold text-foreground">Eagle Eyes</div>
                            <div className="text-[11px] text-muted-foreground -mt-0.5">My Account</div>
                        </div>
                    </Link>
                    <nav className="hidden md:flex items-center gap-1">
                        {navs.map((n) => {
                            const Icon = n.icon;
                            const active = location.pathname + (location.search) === n.to || location.pathname === n.to.split('?')[0];
                            return (
                                <Link key={n.to} to={n.to} data-testid={n.testid} className={`flex items-center gap-1.5 px-3 py-2 rounded-md text-sm font-medium ${active ? 'bg-secondary text-foreground' : 'text-muted-foreground hover:bg-secondary hover:text-foreground'}`}>
                                    <Icon className="h-4 w-4" /> {n.label}
                                </Link>
                            );
                        })}
                    </nav>
                    <div className="flex items-center gap-2">
                        <InstallAppButton testid="user-install-app-button" variant="default" className="bg-[#1F9D8A] hover:bg-[#18806f] text-white border-0" />
                        <div className="hidden sm:block text-right">
                            <div className="text-xs text-muted-foreground">{user?.email}</div>
                        </div>
                        <Button variant="outline" size="sm" onClick={logout} data-testid="user-logout-button" className="gap-1.5">
                            <LogOut className="h-3.5 w-3.5" /> Logout
                        </Button>
                    </div>
                </div>
            </header>
            <main className="flex-1">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">{children}</div>
            </main>
            <nav className="md:hidden sticky bottom-0 z-30 bg-white border-t border-border">
                <div className="grid grid-cols-5 gap-1 p-2">
                    {navs.map((n) => {
                        const Icon = n.icon;
                        return (
                            <Link key={n.to} to={n.to} data-testid={`${n.testid}-mobile`} className="flex flex-col items-center gap-0.5 py-1.5 rounded-md hover:bg-secondary">
                                <Icon className="h-4 w-4" />
                                <span className="text-[9px] font-medium text-center leading-tight">{n.label}</span>
                            </Link>
                        );
                    })}
                </div>
            </nav>
        </div>
    );
}
